import 'expo-dev-client';
import 'shared/styles/global.css';

import Constants from 'expo-constants';
import { loadAsync } from 'expo-font';
import { NavigationBar } from 'expo-navigation-bar';
import { router, Slot, useNavigationContainerRef } from 'expo-router';
import { NavigationContainerRefWithCurrent } from 'expo-router/react-navigation';
import { setBackgroundColorAsync } from 'expo-system-ui';
import React, { StrictMode, useEffect } from 'react';
import { StyleSheet, useColorScheme } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import OfflineModeToggle from 'shared/components/dev/OfflineModeToggle';
import Providers from 'shared/components/Providers';
import { isAndroid, isLocalhost, isWeb } from 'shared/constants/platform';
import useSentryRoutingInstrumentation from 'shared/hooks/sentry/useSentryRoutingInstrumentation';
import theme from 'shared/styles/theme';
import { VaroFontMap } from 'shared/styles/typography';
import { lockMobileToPortraitIfPossible } from 'shared/utils/screenOrientation';
import { init as sentryInit } from 'shared/utils/sentry/sentry';
import sentryWrap from 'shared/utils/sentry/sentryWrap';

sentryInit();

// Screens are frequently opened as the navigation root (deep links, redirects),
// where a bare router.back() dispatches an unhandled GO_BACK. Guard the shared
// imperative router.back() once so every call site no-ops when it cannot go back,
// mirroring the existing navigateBack() helper.
const originalRouterBack = router.back;
router.back = () => {
  if (router.canGoBack()) {
    originalRouterBack();
  }
};

function Root() {
  const navContainerRef = useNavigationContainerRef();
  const colorScheme = useColorScheme();

  useEffect(() => {
    if (isAndroid) {
      const backgroundColor = colorScheme === 'dark' ? 'black' : 'white';
      void setBackgroundColorAsync(backgroundColor);

      const buttonColor = colorScheme === 'dark' ? 'light' : 'dark';
      void NavigationBar.setStyle(buttonColor);
    }
  }, [colorScheme]);

  useSentryRoutingInstrumentation(
    // TODO: Awaiting a fix from Sentry
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    navContainerRef as NavigationContainerRefWithCurrent<{}>,
  );

  useEffect(() => {
    const lockDefaultOrientation = async () => {
      await lockMobileToPortraitIfPossible();
    };
    void lockDefaultOrientation();
  }, []);

  if (isWeb) {
    loadAsync(VaroFontMap)
      .then(() => {
        if (isLocalhost) {
          console.warn('Fonts loaded');
        }
      })
      .catch((error) => {
        if (isLocalhost) {
          console.error('Error loading fonts', error);
        }
      });
  }

  if (isWeb) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    require('shared/utils/polyfill/polyfills');
  }

  return (
    <GestureHandlerRootView style={styles.layoutContainer}>
      <StrictMode>
        <Providers>
          <Slot />
          {__DEV__ ? <OfflineModeToggle /> : null}
        </Providers>
      </StrictMode>
    </GestureHandlerRootView>
  );
}

let AppEntryPoint = Root;

if (
  process.env.NODE_ENV === 'development' &&
  Constants.expoConfig?.extra?.storybookEnabled === '1'
) {
  AppEntryPoint = require('.rnstorybook').default as () => React.JSX.Element;
}

const styles = StyleSheet.create({
  layoutContainer: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});

export default sentryWrap(AppEntryPoint);
